import { useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { todayIso, addMonths, fmtDayHeader, fmtMonth } from "@/components/calendar/calendarUtils";
import { RecitalStrip } from "@/components/shows/MyRecitals";

const TAGS = {
  moved: "bg-amber-100 text-amber-900 border-amber-300",
  recovery: "bg-sky-100 text-sky-900 border-sky-300",
  cancelled: "bg-red-100 text-red-800 border-red-300",
};

const StudentLesson = ({ x, today }) => {
  const off = x.status === "cancelled";
  const past = x.date < today;
  const isToday = x.date === today;
  return (
    <li data-testid={`my-lesson-${x.id}`} className={`rounded-xl border p-4 ${isToday ? "border-primary bg-primary/5" : "border-border bg-white"} ${off ? "opacity-55" : past ? "opacity-70" : ""}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-lg font-semibold leading-snug">
        <span className={`first-letter:uppercase ${off ? "line-through" : ""}`} data-testid={`my-lesson-date-${x.id}`}>{fmtDayHeader(x.date)}</span>
        <span className={off ? "line-through" : ""} data-testid={`my-lesson-time-${x.id}`}>{x.start_time}–{x.end_time}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {isToday && <span className="text-xs font-semibold uppercase tracking-wide rounded-full bg-primary text-primary-foreground px-2.5 py-1" data-testid="my-lesson-today">Oggi</span>}
        {x.status === "moved" && <span data-testid={`my-lesson-status-${x.id}`} className={`text-sm font-semibold rounded-full border px-2.5 py-0.5 ${TAGS.moved}`}>Spostata{x.original_date ? ` da ${fmtDayHeader(x.original_date)}` : ""}{x.original_start_time && x.original_start_time !== x.start_time ? ` (${x.original_start_time})` : ""}</span>}
        {x.status === "recovery" && <span data-testid={`my-lesson-status-${x.id}`} className={`text-sm font-semibold rounded-full border px-2.5 py-0.5 ${TAGS.recovery}`}>Recupero</span>}
        {off && <span data-testid={`my-lesson-status-${x.id}`} className={`text-sm font-semibold rounded-full border px-2.5 py-0.5 ${TAGS.cancelled}`}>Annullata</span>}
      </div>
    </li>
  );
};

// Calendario del proprio corso: caricato solo all'apertura della sezione
export const StudentCalendar = ({ year, isCurrent }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const today = todayIso();

  const toggle = (o) => {
    setOpen(o);
    if (!o || data) return;
    api.get("/me/lessons", { params: { year } }).then((r) => {
      const ls = r.data.lessons;
      const cur = today.slice(0, 7);
      const inRange = ls.length && cur >= ls[0].date.slice(0, 7) && cur <= ls[ls.length - 1].date.slice(0, 7);
      setMonth(isCurrent && (inRange || !ls.length) ? cur : ls.length ? ls[0].date.slice(0, 7) : cur);
      setData(r.data);
    }).catch(() => setData({ visible: false, lessons: [] }));
  };

  const lessons = data?.lessons || [];
  const first = lessons[0]?.date.slice(0, 7);
  const last = lessons[lessons.length - 1]?.date.slice(0, 7);
  const items = useMemo(() => lessons.filter((x) => x.date.slice(0, 7) === month), [lessons, month]);
  const go = (n) => setMonth((m) => addMonths(`${m}-01`, n).slice(0, 7));
  const available = data && data.visible && lessons.length > 0;

  return (
    <Card className="bg-card border-border" data-testid="my-calendar">
      <Collapsible open={open} onOpenChange={toggle}>
        <CollapsibleTrigger data-testid="my-calendar-toggle" className="w-full p-5 flex items-center justify-between gap-3 text-left min-h-[64px]">
          <span className="text-2xl flex items-center gap-2"><CalendarDays size={20} className="text-primary shrink-0" /> Calendario lezioni</span>
          <ChevronDown size={20} className={`text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-4 sm:px-5 pb-5 space-y-4">
          {data && <RecitalStrip recitals={data.recitals} />}
          {!data ? <p className="text-muted-foreground" data-testid="my-calendar-loading">Caricamento…</p>
            : !available ? <p className="text-muted-foreground" data-testid="my-calendar-unavailable">Il calendario non è ancora disponibile</p> : (
              <>
                <p className="text-sm text-muted-foreground break-words" data-testid="my-calendar-course">Corso <strong className="text-foreground">{data.course}</strong>{data.teacher_name ? <> · Docente <strong className="text-foreground" data-testid="my-calendar-teacher">{data.teacher_name}</strong></> : null}{!isCurrent && " · sola consultazione"}</p>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="outline" className="h-11 w-11 shrink-0" data-testid="my-calendar-prev" disabled={!first || month <= first} onClick={() => go(-1)} aria-label="Mese precedente"><ChevronLeft size={20} /></Button>
                  <h3 className="flex-1 text-center text-xl font-semibold" data-testid="my-calendar-month">{fmtMonth(`${month}-01`)}</h3>
                  <Button size="icon" variant="outline" className="h-11 w-11 shrink-0" data-testid="my-calendar-next" disabled={!last || month >= last} onClick={() => go(1)} aria-label="Mese successivo"><ChevronRight size={20} /></Button>
                </div>
                {isCurrent && (
                  <div className="text-center"><Button variant="outline" className="h-11 px-6" data-testid="my-calendar-today" disabled={month === today.slice(0, 7)} onClick={() => setMonth(today.slice(0, 7))}>Oggi</Button></div>
                )}
                {items.length === 0 ? <p className="text-center text-muted-foreground py-6" data-testid="my-calendar-empty-month">Nessuna lezione in questo mese</p>
                  : <ul className="space-y-3">{items.map((x) => <StudentLesson key={x.id} x={x} today={today} />)}</ul>}
              </>
            )}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
